import { timestampDate } from '@bufbuild/protobuf/wkt'
import pluralize from 'pluralize'

import { ControlWithSignalsAndTimeline } from '@/api/export-company.hook'
import { Artifact } from '@/gen/artifacts/v1/model_pb'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { GetAlertResponse } from '@/gen/inventory/v1/alert_service_pb'
import { BusinessOwner, Company, InherentRiskGroup } from '@/gen/inventory/v1/company_service_pb'
import { RiskLevel } from '@/gen/inventory/v1/risk_pb'

import { SECTION_LABELS, WHY_THIS_MATTERS_LABEL } from '@/const/label'

import { scoresAsValidatedControlStatus } from '@/lib/control-status'
import { computeCategorySeverities } from '@/lib/risk/compute-category-severities'
import { cn } from '@/lib/ui/style-helpers'

import { SignalDetailsHeader } from '@/pages/alert-details/signal-header'
import { CompanyArtifactTable } from '@/pages/company-drawer/artifact/company-artifact-table'
import { InherentRiskProfile } from '@/pages/company-drawer/company-overview/inherent-risk-profile/inherent-risk-profile'
import { CompanyProfile } from '@/pages/company-drawer/components/company-profile'
import { EXPORT_CONTENT_LABELS } from '@/pages/export-content/labels'
import { ExportSectionHeading } from '@/pages/past-assessment/export-section-heading'

import { Separator } from '@/components/lemmala-design-system/separator/separator'
import { TextWithIcon } from '@/components/lemmala-design-system/text-with-icon/text-with-icon'
import { AnalysisQuotes } from '@/components/shared/analysis-quotes/analysis-quotes'
import { signalDetailQuotes } from '@/components/shared/analysis-quotes/utils'
import { CompanyStatusBadge } from '@/components/shared/company-status-badge/company-status-badge'
import { ControlEvidence } from '@/components/shared/control-evidence/v3/control-evidence'
import { ControlStatusBadge } from '@/components/shared/control-status-badge/control-status-badge'
import { AlertStatusSymbol } from '@/components/shared/icons/alert-status'
import { LastChanged } from '@/components/shared/last-changed/last-changed'
import { Markdown } from '@/components/shared/markdown/markdown'
import { RiskBadge } from '@/components/shared/risk-badge/risk-badge'
import { SeverityDistribution } from '@/components/shared/severity-distribution/severity-distribution'

import { EXPORT_COMPANY_CONTENT_LABELS } from './labels'

export type ExportCompanyContentProps = {
  companyId: string
  profile?: Company['profile']
  status: Company['status']
  statusLastUpdate?: Company['statusLastUpdate']
  risk: RiskLevel
  alertsByLevel: Company['alertsByLevel']
  businessOwner?: BusinessOwner
  inherentRiskGroups: InherentRiskGroup[]
  engagementModeEnabled: boolean
  artifacts: Artifact[]
  artifactsLoading: boolean
  controlsByCategory: Record<string, ControlWithSignalsAndTimeline[]>
  allAlerts: GetAlertResponse[]
}

export const ExportCompanyContent = ({
  companyId,
  profile,
  status,
  statusLastUpdate,
  risk,
  alertsByLevel,
  businessOwner,
  inherentRiskGroups,
  engagementModeEnabled,
  artifacts,
  artifactsLoading,
  controlsByCategory,
  allAlerts,
}: ExportCompanyContentProps) => {
  return (
    <>
      <div className='mb-10 bg-main-background-muted py-10'>
        <div className='mx-8'>
          <div className='grid w-fit grid-cols-[min-content,auto] items-center gap-x-20 gap-y-4.5'>
            <h2 className='text-nowrap text-3xl font-bold'>{profile?.name}</h2>
            <span></span>

            <span className='text-nowrap text-sm font-bold'>
              {EXPORT_COMPANY_CONTENT_LABELS.assessmentStatusHeading}
            </span>
            <div className='flex items-center gap-2'>
              <CompanyStatusBadge className='pl-0' status={status} />
              {statusLastUpdate && (
                <LastChanged withTimeCount={false} lastChanged={timestampDate(statusLastUpdate)} />
              )}
            </div>
            <span className='text-sm font-bold'>{EXPORT_CONTENT_LABELS.headings.inherentRisk}</span>
            <RiskBadge riskLevel={risk} />
            <span className='text-sm font-bold'>{EXPORT_CONTENT_LABELS.headings.description}</span>
            <p>{profile?.longDescription}</p>
            <TextWithIcon
              className='text-sm font-bold uppercase'
              text={SECTION_LABELS.alerts}
              icon={<AlertStatusSymbol />}
            />
            <SeverityDistribution countByLevel={alertsByLevel} />

            {businessOwner?.name && (
              <>
                <span className='text-nowrap text-sm font-bold'>
                  {EXPORT_CONTENT_LABELS.headings.businessOwner}
                </span>
                <span className='ml-1 text-md'>
                  {businessOwner.name} {businessOwner.email && `(${businessOwner.email})`}
                </span>
              </>
            )}
          </div>
        </div>
        <Separator className='my-12' />
        <div className='mx-8'>{profile && <CompanyProfile info={profile} />}</div>
      </div>
      <div className='break-after-page' />
      <div className='my-10' />
      <div className='mx-8 flex items-center rounded border'>
        <InherentRiskProfile
          companyId={companyId}
          companyRisk={risk}
          showCta={false}
          irqCategorySeverities={computeCategorySeverities(inherentRiskGroups)}
          engagementModeEnabled={engagementModeEnabled}
          isIrqTemplateOutdated={false}
        />
      </div>
      <div className='break-after-page' />
      <ExportSectionHeading
        heading={EXPORT_CONTENT_LABELS.sections.thirdPartyArtifacts}
        contained={false}
      />
      <div className='mx-8'>
        <CompanyArtifactTable
          isExport
          readOnly
          data={artifacts}
          isLoading={artifactsLoading}
          containerClassName=''
        />
      </div>
      <div className='break-after-page' />
      <div className='mx-8'>
        <ExportControlContent controlsByCategory={controlsByCategory} artifacts={artifacts} />
      </div>

      <div className='break-after-page' />
      <div className='my-10' />
      <ExportSectionHeading heading={SECTION_LABELS.alerts} contained={false} />

      <div className='mx-8'>
        {allAlerts.map((data) => (
          <div key={data?.alert?.id} className='my-10 break-after-page last:break-after-avoid'>
            {data?.alert && (
              <SignalDetailsHeader
                titleVariant={'showAll'}
                readOnly
                hideSummary
                id={data.alert.id}
                title={data.alert.signal?.title ?? ''}
                subtitle={data.alert.signal?.subtitle ?? ''}
                visible={data.alert.signal?.visible}
                signalType={data.alert.signal?.signalType}
              />
            )}
            <div className='mx-8'>
              <div>
                <h1 className='font-semibold leading-10'>
                  {EXPORT_CONTENT_LABELS.alertDetails.description}
                </h1>
                <Markdown>{data?.detail?.documentation?.description}</Markdown>
              </div>
              <br />
              <div>
                <h1 className='font-semibold leading-10'>{WHY_THIS_MATTERS_LABEL}</h1>
                <Markdown>{data?.detail?.documentation?.risk}</Markdown>
              </div>
              {signalDetailQuotes(data?.detail).length > 0 && (
                <>
                  <Separator className='my-12' />
                  <AnalysisQuotes quotes={signalDetailQuotes(data?.detail)} readonly />
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </>
  )
}

type ExportControlContentProps = {
  controlsByCategory: Record<string, ControlWithSignalsAndTimeline[]>
  artifacts: Artifact[]
}

export const ExportControlContent = ({ controlsByCategory }: ExportControlContentProps) => {
  const categoryEntries = Object.entries(controlsByCategory)
  if (categoryEntries.length === 0) return null

  // Destructure first category and the rest
  const [[firstCategory, firstControls], ...restCategories] = categoryEntries

  // Destructure first control and the rest
  const [firstControl, ...restControls] = firstControls

  return (
    <>
      {/* Group title, first category header, and first control together */}
      <div className='break-inside-avoid'>
        <ExportSectionHeading
          heading={EXPORT_CONTENT_LABELS.sections.thirdPartyControlsAndAlerts}
          contained={false}
        />
        <div className='mb-28'>
          <div className='mt-4 rounded bg-main-background-muted px-10 py-5 font-bold uppercase'>
            <span className='text-xs'>{EXPORT_CONTENT_LABELS.headings.controlCategory}</span>
            <h3 className='text-xl'>{firstCategory}</h3>
            <span className='text-xs text-main-foreground-tertiary'>
              {pluralize(EXPORT_CONTENT_LABELS.controlSingular, firstControls.length, true)}
            </span>
          </div>
          <div className='mt-6 flex flex-col gap-6'>
            {firstControl && (
              <div className='break-inside-avoid' key={firstControl.control?.id}>
                <div
                  className={cn('rounded border p-4', {
                    'border-main-border-error': firstControl.control?.status === ControlStatus.GAP,
                    'border-main-border-success': scoresAsValidatedControlStatus(
                      firstControl.control?.status,
                    ),
                    'border-main-border-warning':
                      firstControl.control?.status === ControlStatus.NO_EVIDENCE,
                  })}
                >
                  <div className='mb-2 flex flex-col gap-1'>
                    <span className='shrink-0 text-lg font-bold'>
                      {firstControl.control?.title}
                    </span>
                    <span className='text-lg font-bold'>
                      {firstControl.controlDefinition || firstControl.control?.description}
                    </span>
                  </div>
                  <div className='flex w-fit gap-2'>
                    <ControlStatusBadge
                      status={firstControl.control?.status || ControlStatus.VALIDATED}
                    />
                  </div>
                  <div className='w-full p-6'>
                    <ControlEvidence
                      timeline={firstControl.timeline}
                      control={firstControl}
                      signals={firstControl.signals}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {/* Render the rest of the controls in the first category */}
      {restControls.length > 0 && (
        <div className='mb-28'>
          <div className='mt-6 flex flex-col gap-6'>
            {restControls.map((controlWithSignal) => (
              <div className='break-inside-avoid' key={controlWithSignal.control?.id}>
                <div
                  className={cn('rounded border p-4', {
                    'border-main-border-error':
                      controlWithSignal.control?.status === ControlStatus.GAP,
                    'border-main-border-success': scoresAsValidatedControlStatus(
                      controlWithSignal.control?.status,
                    ),
                    'border-main-border-warning':
                      controlWithSignal.control?.status === ControlStatus.NO_EVIDENCE,
                  })}
                >
                  <div className='mb-2 flex flex-col gap-1'>
                    <span className='shrink-0 text-lg font-bold'>
                      {controlWithSignal.control?.title}
                    </span>
                    <span className='text-lg font-bold'>
                      {controlWithSignal.controlDefinition ||
                        controlWithSignal.control?.description}
                    </span>
                  </div>
                  <div className='flex w-fit gap-2'>
                    <ControlStatusBadge
                      status={controlWithSignal.control?.status || ControlStatus.VALIDATED}
                    />
                  </div>
                  <div className='w-full p-6'>
                    <ControlEvidence
                      timeline={controlWithSignal.timeline}
                      control={controlWithSignal}
                      signals={controlWithSignal.signals}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Render the rest of the categories */}
      {restCategories.map(([category, controls], categoryIndex) => (
        <div
          key={category}
          className={cn('mb-28 break-inside-avoid', {
            'break-after-page': categoryIndex < restCategories.length - 1,
          })}
        >
          <div className='mt-4 rounded bg-main-background-muted px-10 py-5 font-bold uppercase'>
            <span className='text-xs'>{EXPORT_CONTENT_LABELS.headings.controlCategory}</span>
            <h3 className='text-xl'>{category}</h3>
            <span className='text-xs text-main-foreground-tertiary'>
              {pluralize(EXPORT_CONTENT_LABELS.controlSingular, controls.length, true)}
            </span>
          </div>
          <div className='mt-6 flex flex-col gap-6'>
            {controls.map((controlWithAlert) => (
              <div className='break-inside-avoid' key={controlWithAlert.control?.id}>
                <div
                  className={cn('rounded border p-4', {
                    'border-main-border-error':
                      controlWithAlert.control?.status === ControlStatus.GAP,
                    'border-main-border-success': scoresAsValidatedControlStatus(
                      controlWithAlert.control?.status,
                    ),
                    'border-main-border-warning':
                      controlWithAlert.control?.status === ControlStatus.NO_EVIDENCE,
                  })}
                >
                  <div className='mb-2 flex flex-col gap-1'>
                    <span className='shrink-0 text-lg font-bold'>
                      {controlWithAlert.control?.title}
                    </span>
                    <span className='text-lg font-bold'>
                      {controlWithAlert.controlDefinition || controlWithAlert.control?.description}
                    </span>
                  </div>
                  <div className='flex w-fit gap-2'>
                    <ControlStatusBadge
                      status={controlWithAlert.control?.status || ControlStatus.VALIDATED}
                    />
                  </div>
                  <div className='w-full p-6'>
                    <ControlEvidence
                      timeline={controlWithAlert.timeline}
                      control={controlWithAlert}
                      signals={controlWithAlert.signals}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  )
}
